import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useState } from "react";
import { useDataProvider, useRecordContext, useRefresh } from "react-admin";
import { InstructorOrEmployeeGrantsInput } from "../../inputs/InstructorOrEmployeeGrantsInput";
import { Column } from "../../misc/Column";
import { FormCard } from "../../misc/FormCard";
import { Employee } from "../../providers/employeesProvider";
import { UserAvatarForm } from "../../misc/UserAvatarForm";
import { UserRolesForm } from "../../misc/UserRolesForm";
import { cardTitleStyle, overviewCardStyle } from "../../misc/backofficeTheme";
import { grants } from "../../backoffice.access_control";
import { DrivingSchoolUserRolesForm } from "../drivingSchool/DrivingSchoolUserRolesForm";
import { TextInput } from "react-admin";
import { ResetPasswordSection } from "../../misc/ResetPasswordSection";

export function EmployeeAdminForm() {
  const isAutovioAdmin = grants.has("admin");
  const canManageUsers = isAutovioAdmin || grants.has("manageUsers");

  return (
    <Grid container spacing="30px">
      <Grid item xs={6}>
        <Column gap="30px">
          <_EmployeeDetailsEditForm />
          {isAutovioAdmin && <UserAvatarForm<Employee> />}
          <EmployeeResetPasswordSection />
          {canManageUsers && <EmployeeStatusSection />}
          {isAutovioAdmin ? (
            <UserRolesForm resource="employees" />
          ) : canManageUsers ? (
            <DrivingSchoolUserRolesForm />
          ) : null}
        </Column>
      </Grid>
      {canManageUsers && (
        <Grid item xs={6}>
          <Column gap="30px">
            <FormCard title="Berechtigungen">
              <InstructorOrEmployeeGrantsInput scope="drivingSchoolManagerAndAutovioAdmin" />
            </FormCard>
            {isAutovioAdmin && (
              <FormCard title="Berechtigungen (Nur AUTOVIO)">
                <InstructorOrEmployeeGrantsInput scope="autovioAdminOnly" />
              </FormCard>
            )}
          </Column>
        </Grid>
      )}
    </Grid>
  );
}

function _EmployeeDetailsEditForm() {
  return (
    <FormCard title="Persönliche Daten">
      <Column>
        <TextInput label="Vorname" source="firstName" fullWidth />
        <TextInput label="Nachname" source="lastName" fullWidth />
        <TextInput label="Telefonnummer" source="phoneNumber" fullWidth />
      </Column>
    </FormCard>
  );
}

function EmployeeStatusSection() {
  const employee = useRecordContext<Employee>();
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!employee) {
    return null;
  }

  const shouldDisable = !employee.disabled;

  async function handleConfirm() {
    setLoading(true);
    try {
      await runSafely(
        () =>
          dataProvider.update("employees", {
            id: employee.id,
            data: { disabled: shouldDisable },
            previousData: employee,
          }),
        {
          successMessage: shouldDisable
            ? `${employee.name} wurde erfolgreich deaktiviert.`
            : `${employee.name} wurde erfolgreich aktiviert.`,
          errorMessage: shouldDisable
            ? `Fehler beim Deaktivieren von ${employee.name}.`
            : `Fehler beim Aktivieren von ${employee.name}.`,
          onSuccess: () => {
            setDialogOpen(false);
            refresh();
          },
        },
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box sx={overviewCardStyle}>
      <Typography sx={cardTitleStyle}>Account</Typography>
      <Typography sx={{ mb: 2 }}>
        Status: <strong>{employee.disabled ? "Inaktiv" : "Aktiv"}</strong>
      </Typography>
      <Button variant="outlined" color={employee.disabled ? "success" : "warning"} onClick={() => setDialogOpen(true)}>
        {employee.disabled ? "Aktivieren" : "Deaktivieren"}
      </Button>
      <Dialog open={dialogOpen} onClose={() => !loading && setDialogOpen(false)}>
        <DialogTitle>{employee.disabled ? "Mitarbeiter aktivieren" : "Mitarbeiter deaktivieren"}</DialogTitle>
        <DialogContent sx={{ pt: 2, minWidth: 400 }}>
          <Typography>
            {employee.disabled
              ? `Soll ${employee.name} wirklich aktiviert werden?`
              : `Soll ${employee.name} wirklich deaktiviert werden?`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <LoadingButton variant="outlined" onClick={() => setDialogOpen(false)} disabled={loading}>
            Abbrechen
          </LoadingButton>
          <LoadingButton variant="contained" onClick={handleConfirm} loading={loading}>
            {employee.disabled ? "Aktivieren" : "Deaktivieren"}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function EmployeeResetPasswordSection() {
  const employee = useRecordContext<Employee>();
  if (!employee) {
    return null;
  }

  return (
    <ResetPasswordSection
      name={employee.name}
      email={employee.email}
      noEmailErrorMessage="Für diese Bürokraft ist keine E-Mail-Adresse hinterlegt."
    />
  );
}
